<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Ventas pendientes de factura</h2>
        <p class="text-muted mb-0">
          Ventas sin documento cobradas con ticket. Se facturan en el sistema externo, en una factura
          consolidada; después se marcan acá con el número de esa factura.
        </p>
      </div>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'MaintenanceFiscalDashboard' }">
        Volver a Fiscal
      </router-link>
    </header>

    <div v-if="!puedeGestionar" class="alert alert-warning mb-0">
      Esta sección está disponible sólo para usuarios con el permiso <code>gestionar_ventas_pendientes_factura</code>.
    </div>

    <template v-else>
      <div class="card shadow-sm">
        <div class="card-body row g-3 align-items-end">
          <div class="col-12 col-sm-4 col-lg-3">
            <label class="form-label small mb-1" for="vpfEstado">Estado</label>
            <select id="vpfEstado" v-model="filtros.estado" class="form-select form-select-sm">
              <option value="pendiente">Pendientes de factura</option>
              <option value="facturada">Facturadas en otro sistema</option>
            </select>
          </div>
          <div class="col-6 col-sm-4 col-lg-2">
            <label class="form-label small mb-1" for="vpfDesde">Desde</label>
            <input id="vpfDesde" v-model="filtros.desde" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-6 col-sm-4 col-lg-2">
            <label class="form-label small mb-1" for="vpfHasta">Hasta</label>
            <input id="vpfHasta" v-model="filtros.hasta" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-12 col-lg-5 d-flex gap-2 flex-wrap justify-content-lg-end">
            <button class="btn btn-primary btn-sm" :disabled="cargando" @click="cargar">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>Buscar
            </button>
            <button class="btn btn-outline-secondary btn-sm" :disabled="cargando || !ventas.length" @click="exportar">
              <i class="bi bi-filetype-csv me-1"></i>Exportar CSV
            </button>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2 mb-0">{{ error }}</div>

      <!-- RN-VI7: lo que se carga en la factura consolidada -->
      <div v-if="listado" class="row g-3">
        <div class="col-6 col-md-4 col-xl-2" v-for="item in tarjetasResumen" :key="item.label">
          <div class="card h-100">
            <div class="card-body py-2">
              <div class="text-muted small">{{ item.label }}</div>
              <div class="fw-bold">{{ formatoGs(item.valor) }}</div>
            </div>
          </div>
        </div>
        <div v-if="listado.total_ventas !== listado.resumen.total" class="col-12">
          <div class="alert alert-warning py-2 mb-0 small">
            El total de las ventas ({{ formatoGs(listado.total_ventas) }}) y el de su detalle
            ({{ formatoGs(listado.resumen.total) }}) difieren por redondeo. Revisá antes de facturar.
          </div>
        </div>
      </div>

      <div v-if="listado" class="card shadow-sm">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div class="small text-muted">
              {{ ventas.length }} venta(s) · {{ seleccionadas.length }} seleccionada(s)
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <button
                v-if="filtros.estado === 'pendiente'"
                class="btn btn-success btn-sm"
                :disabled="!seleccionadas.length"
                @click="abrirMarcar"
              >
                <i class="bi bi-check2-square me-1"></i>Marcar como facturadas
              </button>
              <button
                v-else
                class="btn btn-outline-danger btn-sm"
                :disabled="!seleccionadas.length"
                @click="abrirDesmarcar"
              >
                <i class="bi bi-arrow-counterclockwise me-1"></i>Desmarcar
              </button>
            </div>
          </div>

          <div v-if="!ventas.length" class="text-muted small">No hay ventas para este filtro.</div>

          <div v-else class="table-responsive">
            <table class="table table-sm align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th style="width: 2rem;">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="todasSeleccionadas"
                      aria-label="Seleccionar todas"
                      @change="alternarTodas"
                    />
                  </th>
                  <th>Venta</th>
                  <th>Fecha</th>
                  <th>Detalle</th>
                  <th v-if="filtros.estado === 'facturada'">Factura externa</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in ventas" :key="v.id_venta">
                  <td>
                    <input
                      v-model="seleccionadas"
                      type="checkbox"
                      class="form-check-input"
                      :value="v.id_venta"
                      :aria-label="`Seleccionar la venta ${v.nro_factura}`"
                    />
                  </td>
                  <td class="text-nowrap">{{ v.nro_factura }}</td>
                  <td class="text-nowrap small">{{ formatoFecha(v.fecha_emision) }}</td>
                  <td class="small">
                    <div v-for="(l, i) in v.lineas" :key="i">
                      {{ l.cantidad }} {{ l.unidad }} × {{ l.producto || l.codigo }}
                      <span class="text-muted">({{ l.tasa_iva ? `IVA ${l.tasa_iva} %` : 'exento' }})</span>
                    </div>
                  </td>
                  <td v-if="filtros.estado === 'facturada'" class="small">
                    <div>{{ v.factura_externa_referencia }}</div>
                    <div class="text-muted">
                      {{ v.factura_externa_fecha ? formatoFecha(v.factura_externa_fecha, false) : '' }}
                      <span v-if="v.factura_externa_marcada_por_usuario">· {{ v.factura_externa_marcada_por_usuario }}</span>
                    </div>
                  </td>
                  <td class="text-end fw-semibold text-nowrap">{{ formatoGs(v.total_iva_incluido) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <!-- Marcar (RN-VI6) -->
    <div v-if="modal === 'marcar'" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Marcar {{ seleccionadas.length }} venta(s) como facturadas</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrarModal"></button>
          </div>
          <div class="modal-body">
            <p class="small text-muted">
              Total seleccionado: <strong>{{ formatoGs(totalSeleccionado) }}</strong>. Indicá la factura del sistema externo
              que las incluye.
            </p>
            <label class="form-label small mb-1" for="vpfReferencia">Número de la factura externa</label>
            <input id="vpfReferencia" v-model.trim="formMarcar.referencia" maxlength="50" class="form-control mb-3" />
            <label class="form-label small mb-1" for="vpfFecha">Fecha de la factura externa</label>
            <input id="vpfFecha" v-model="formMarcar.fecha" type="date" class="form-control" />
            <div v-if="errorModal" class="alert alert-danger py-2 mt-3 mb-0 small">{{ errorModal }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline-secondary" :disabled="guardando" @click="cerrarModal">Cancelar</button>
            <button class="btn btn-success" :disabled="guardando || !formMarcar.referencia || !formMarcar.fecha" @click="confirmarMarcar">
              <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>Marcar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Desmarcar (RN-VI6) -->
    <div v-if="modal === 'desmarcar'" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Desmarcar {{ seleccionadas.length }} venta(s)</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="cerrarModal"></button>
          </div>
          <div class="modal-body">
            <p class="small text-muted">Vuelven a pendientes de factura. Queda registrado quién y por qué.</p>
            <label class="form-label small mb-1" for="vpfMotivo">Motivo</label>
            <input id="vpfMotivo" v-model.trim="formDesmarcar.motivo" maxlength="150" class="form-control" />
            <div v-if="errorModal" class="alert alert-danger py-2 mt-3 mb-0 small">{{ errorModal }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline-secondary" :disabled="guardando" @click="cerrarModal">Cancelar</button>
            <button class="btn btn-danger" :disabled="guardando || formDesmarcar.motivo.length < 5" @click="confirmarDesmarcar">
              <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>Desmarcar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="modal" class="modal-backdrop fade show"></div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { hasPermission } from '@/services/authSessionService'
import ventasPendientesFacturaService from '@/services/ventasPendientesFacturaService'

const puedeGestionar = computed(() => hasPermission('gestionar_ventas_pendientes_factura'))

const hoy = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const filtros = reactive({ estado: 'pendiente', desde: '', hasta: '' })
const listado = ref(null)
const cargando = ref(false)
const error = ref('')
const seleccionadas = ref([])
const modal = ref(null)
const guardando = ref(false)
const errorModal = ref('')
const formMarcar = reactive({ referencia: '', fecha: hoy() })
const formDesmarcar = reactive({ motivo: '' })

const ventas = computed(() => listado.value?.ventas || [])
const todasSeleccionadas = computed(() => ventas.value.length > 0 && seleccionadas.value.length === ventas.value.length)
const totalSeleccionado = computed(() => ventas.value
  .filter((v) => seleccionadas.value.includes(v.id_venta))
  .reduce((acc, v) => acc + Math.round(Number(v.total_iva_incluido) || 0), 0))

const tarjetasResumen = computed(() => {
  const r = listado.value?.resumen || {}
  return [
    { label: 'Total', valor: r.total },
    { label: 'Exentas', valor: r.exentas },
    { label: 'Gravado 5 % (IVA incl.)', valor: r.gravado5 },
    { label: 'IVA 5 %', valor: r.iva5 },
    { label: 'Gravado 10 % (IVA incl.)', valor: r.gravado10 },
    { label: 'IVA 10 %', valor: r.iva10 }
  ]
})

const mensajeDe = (e, fallback) => e?.response?.data?.message || e?.message || fallback

const formatoGs = (n) => `₲ ${new Intl.NumberFormat('es-PY').format(Math.round(Number(n) || 0))}`
const formatoFecha = (valor, conHora = true) => {
  if (!valor) return ''
  const d = new Date(valor)
  if (Number.isNaN(d.getTime())) return String(valor)
  return conHora ? d.toLocaleString('es-PY') : d.toLocaleDateString('es-PY', { timeZone: 'UTC' })
}

async function cargar() {
  cargando.value = true
  error.value = ''
  try {
    listado.value = await ventasPendientesFacturaService.listar({ ...filtros })
    seleccionadas.value = []
  } catch (e) {
    error.value = mensajeDe(e, 'No se pudo cargar el listado.')
  } finally {
    cargando.value = false
  }
}

async function exportar() {
  try {
    await ventasPendientesFacturaService.descargarCsv({ ...filtros })
  } catch (e) {
    error.value = mensajeDe(e, 'No se pudo exportar el CSV.')
  }
}

function alternarTodas() {
  seleccionadas.value = todasSeleccionadas.value ? [] : ventas.value.map((v) => v.id_venta)
}

function abrirMarcar() {
  formMarcar.referencia = ''
  formMarcar.fecha = hoy()
  errorModal.value = ''
  modal.value = 'marcar'
}

function abrirDesmarcar() {
  formDesmarcar.motivo = ''
  errorModal.value = ''
  modal.value = 'desmarcar'
}

function cerrarModal() {
  if (guardando.value) return
  modal.value = null
}

async function confirmarMarcar() {
  guardando.value = true
  errorModal.value = ''
  try {
    await ventasPendientesFacturaService.marcar({ ids: seleccionadas.value, ...formMarcar })
    guardando.value = false
    modal.value = null
    await cargar()
  } catch (e) {
    errorModal.value = mensajeDe(e, 'No se pudieron marcar las ventas.')
    guardando.value = false
  }
}

async function confirmarDesmarcar() {
  guardando.value = true
  errorModal.value = ''
  try {
    await ventasPendientesFacturaService.desmarcar({ ids: seleccionadas.value, motivo: formDesmarcar.motivo })
    guardando.value = false
    modal.value = null
    await cargar()
  } catch (e) {
    errorModal.value = mensajeDe(e, 'No se pudieron desmarcar las ventas.')
    guardando.value = false
  }
}

onMounted(() => {
  if (puedeGestionar.value) cargar()
})
</script>
